import { useState } from "react";
import {
  Building2,
  Check,
  ChevronsUpDown,
  FolderOpen,
  Funnel,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Switch } from "@/shared/components/ui/switch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import {
  type FilterableAppId,
  FILTERABLE_APPLICATIONS,
} from "../../hooks/useApplicationFilter";
import styles from "./HeaderBar.module.css";

import outlookIcon from "@/assets/applications/OutlookIcon.svg";
import wordIcon from "@/assets/applications/WordIcon.svg";
import excelIcon from "@/assets/applications/ExcelIcon.svg";
import powerpointIcon from "@/assets/applications/PowerpointIcon.svg";
import adobeIcon from "@/assets/applications/AdobeIcon.svg";
import kofaxIcon from "@/assets/applications/KofaxIcon.svg";
import foxitIcon from "@/assets/applications/FoxitIcon.svg";
import zoomIcon from "@/assets/applications/ZoomIcon.svg";
import teamsIcon from "@/assets/applications/TeamsIcon.svg";

const APP_ICONS: Record<FilterableAppId, string> = {
  outlook: outlookIcon,
  word: wordIcon,
  excel: excelIcon,
  powerpoint: powerpointIcon,
  adobe: adobeIcon,
  kofax: kofaxIcon,
  foxit: foxitIcon,
  zoom: zoomIcon,
  teams: teamsIcon,
};

export interface FiltersPopoverMatter {
  matterId: string;
  matterNumber: string;
  matterDescription: string;
  client: string;
}

export interface ClientMatterFilterSlice {
  clients: string[];
  matters: FiltersPopoverMatter[];
  clientFilter: string | null;
  matterFilter: string | null;
  onClientChange: (client: string | null) => void;
  onMatterChange: (matterId: string | null, client: string | null) => void;
}

export interface FiltersPopoverProps {
  isVisible: (appId: FilterableAppId) => boolean;
  onToggleApplication: (appId: FilterableAppId) => void;
  onShowAllApps: () => void;
  onHideAllApps: () => void;
  clientMatter?: ClientMatterFilterSlice;
}

export function FiltersPopover({
  isVisible,
  onToggleApplication,
  onShowAllApps,
  onHideAllApps,
  clientMatter,
}: FiltersPopoverProps) {
  const [open, setOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [matterOpen, setMatterOpen] = useState(false);

  const selectedMatter = clientMatter?.matterFilter
    ? clientMatter.matters.find((m) => m.matterId === clientMatter.matterFilter) ?? null
    : null;

  const hasActive =
    !!clientMatter?.clientFilter ||
    !!clientMatter?.matterFilter ||
    FILTERABLE_APPLICATIONS.some((a) => !isVisible(a.id));

  const clearAll = () => {
    clientMatter?.onClientChange(null);
    onShowAllApps();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Radix doesn't always propagate outside-click events to inner popovers
        // before the outer one unmounts, so reset inner state on close.
        if (!next) {
          setClientOpen(false);
          setMatterOpen(false);
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary relative`}
            >
              <Funnel className="h-4 w-4" />
              {hasActive && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Filters</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <h4 className="font-medium text-sm text-foreground">Filters</h4>
          {hasActive && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
            >
              Clear all
            </button>
          )}
        </div>

        {clientMatter && (
          <div className="px-3 py-2.5 border-b border-border">
            <h5 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Client / Matter
            </h5>
            <div className="space-y-2">
              <Popover open={clientOpen} onOpenChange={setClientOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                  >
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1 text-left">
                      {clientMatter.clientFilter || "All clients"}
                    </span>
                    <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            clientMatter.onClientChange(null);
                            setClientOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !clientMatter.clientFilter ? "opacity-100" : "opacity-0",
                            )}
                          />
                          All clients
                        </CommandItem>
                        {clientMatter.clients.map((c) => (
                          <CommandItem
                            key={c}
                            value={c}
                            onSelect={() => {
                              clientMatter.onClientChange(c);
                              setClientOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                clientMatter.clientFilter === c ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {c}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={matterOpen} onOpenChange={setMatterOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                  >
                    <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1 text-left">
                      {selectedMatter ? selectedMatter.matterNumber : "All matters"}
                    </span>
                    <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search matters..." />
                    <CommandList>
                      <CommandEmpty>No matters found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            clientMatter.onMatterChange(null, null);
                            setMatterOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !clientMatter.matterFilter ? "opacity-100" : "opacity-0",
                            )}
                          />
                          All matters
                        </CommandItem>
                        {clientMatter.matters.map((m) => (
                          <CommandItem
                            key={m.matterId}
                            value={`${m.matterNumber} ${m.matterDescription}`}
                            onSelect={() => {
                              clientMatter.onMatterChange(m.matterId, m.client);
                              setMatterOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                clientMatter.matterFilter === m.matterId
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div className="flex flex-col min-w-0">
                              <span className="truncate text-sm">{m.matterNumber}</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {m.matterDescription} · {m.client}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        <div className="px-3 py-2.5">
          <div className="flex items-center justify-between">
            <h5 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Applications
            </h5>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onShowAllApps}
                className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
              >
                All
              </button>
              <span className="text-xs text-muted-foreground">·</span>
              <button
                type="button"
                onClick={onHideAllApps}
                className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
              >
                None
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Show/hide entries by source app
          </p>
          <div className="space-y-0.5">
            {FILTERABLE_APPLICATIONS.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-secondary"
              >
                <div className="flex items-center gap-3">
                  <img src={APP_ICONS[app.id]} alt={app.displayName} className="w-5 h-5" />
                  <span className="text-sm text-foreground">{app.displayName}</span>
                </div>
                <Switch
                  checked={isVisible(app.id)}
                  onCheckedChange={() => onToggleApplication(app.id)}
                  className="scale-75"
                />
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
